import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Pressable, ScrollView, PanResponder } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole } from '../context/voiceConsoleContext';
import { useLiveCalls } from '../context/liveCallsContext';
import { Button } from '../components/voiceUi';
import Icon from '../components/icon';
import { fmtDur, durWords, inr } from '../utils/format';
import { getCallDetail, getCallHeader, CallDetail, CallHeader } from '../services/voice/voiceService';

// Empty detail — the pre-fetch / no-data state. No mock rows: the header comes
// from the REAL selected call, insights + recording from the API.
const EMPTY_DETAIL: CallDetail = {
  summary: '', products: [], offered: 0, offeredN: 0, accepted: 0, acceptedN: 0,
  hitRate: 0, leadValue: 0, captured: [], unmatched: [], transcript: [],
  recordingUrl: null, durationSeconds: null,
};

const N_BARS = 68;
const SPEEDS = [0.5, 1, 1.25, 1.5, 2];

// Font scale. This is a normal in-app detail page (NOT the LED-wall board), so
// SCALE = 1 keeps text aligned with the other console pages (Call Log etc.).
// The fs() calls remain so sizing stays tunable from one knob if ever needed.
const SCALE = 1;
const fs = (n: number) => Math.round(n * SCALE);

// Deterministic waveform bar heights (verbatim formula from the HTML).
function barHeight(i: number) {
  return 34 + Math.round(52 * Math.abs(Math.sin(i * 1.7) + 0.45 * Math.sin(i * 0.6 + 1)));
}

// Call Detail — fixed overlay at top:56 left:236 (inside the shell). History mode
// with playback + waveform + transcript + insights rail. Pixel-faithful to the HTML.
export default function CallDetailScreen() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { selectedCall, selectedCallHeader, detailKind, closeCall } = useConsole();
  const { transcriptFor, calls } = useLiveCalls();
  // Header from the opener's row snapshot when present; else fetched by id below
  // (Follow-ups "View call detail" / deep-links open with only a call id).
  const [fetchedHeader, setFetchedHeader] = useState<CallHeader | null>(null);
  const call = selectedCallHeader ?? fetchedHeader;
  const liveEntry = calls.find(x => x.id === selectedCall);
  // A call is "live" only while it's STILL in the active SSE set. Opened as live
  // but no longer streaming (ended) → flip to the ended/recorded view so the timer
  // + waveform stop. Once it left the set, it stays ended (openedLive latch avoids
  // flicker if a late frame re-adds it).
  const openedLive = detailKind === 'live';
  const isLive = openedLive && liveEntry != null;
  // Live transcript lines stream over SSE while the call is in progress.
  const liveLines = transcriptFor(openedLive ? selectedCall : null);
  // Load real detail for the selected call; empty shape pre-fetch so UI is identical.
  const [detail, setDetail] = useState(EMPTY_DETAIL);
  const missed = call?.outcome === 'missed';

  // Stable call-start anchor (ms) — taken from the live-calls set (startedAt) so
  // per-line elapsed + the header timer share ONE origin and don't drift.
  const liveStartMs = useRef<number | null>(null);
  if (openedLive && liveStartMs.current == null) {
    liveStartMs.current = liveEntry?.startedAt ?? (Date.now() - (call?.durSec ?? 0) * 1000);
  }
  // Freeze the final elapsed at the instant the call leaves the live set, so the
  // header stops counting at the real end-duration instead of ticking forever.
  const frozenDur = useRef<number | null>(null);
  if (openedLive && !isLive && frozenDur.current == null && liveStartMs.current != null) {
    frozenDur.current = Math.max(1, Math.floor((Date.now() - liveStartMs.current) / 1000));
  }

  // 1s tick so a LIVE call's elapsed time + timestamps count up on screen.
  const [, force] = useState(0);
  useEffect(() => {
    if (!isLive) return;
    const id = setInterval(() => force(n => n + 1), 1000);
    return () => clearInterval(id);
  }, [isLive]);

  // Real recording playback (recorded calls). A hidden <audio> element (RN-Web
  // renders DOM) is the source of truth for playT/dur — no fake timer.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playT, setPlayT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [volume, setVolume] = useState(0.8);

  // Resizable split between the transcript (left) and insights rail (right).
  // `splitPct` is the transcript's share of the row width; drag the divider to
  // adjust. Clamped 30–80% so neither pane collapses. Measured width lets the
  // drag delta map to a percentage.
  const [splitPct, setSplitPct] = useState(58);
  const splitPctRef = useRef(58); // mirrors splitPct for the once-created PanResponder
  splitPctRef.current = splitPct;
  const rowWidth = useRef(0);
  const dragStartPct = useRef(58);
  const dividerPan = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: () => { dragStartPct.current = splitPctRef.current; },
      onPanResponderMove: (_e, g) => {
        const w = rowWidth.current;
        if (!w) return;
        const next = dragStartPct.current + (g.dx / w) * 100;
        setSplitPct(Math.max(30, Math.min(80, next)));
      },
    }),
  ).current;

  // Fetch detail whenever the selected call changes; guard against out-of-order responses.
  useEffect(() => {
    if (!selectedCall) return;
    let alive = true;
    getCallDetail(selectedCall).then(res => { if (alive) setDetail(res.data); }).catch(() => {});
    return () => { alive = false; };
  }, [selectedCall]);

  // When opened without a row snapshot (Follow-ups / deep-link), fetch the header
  // by id so the overlay renders. Skip for live opens (they already carry a header).
  useEffect(() => {
    setFetchedHeader(null);
    if (!selectedCall || selectedCallHeader || detailKind === 'live') return;
    let alive = true;
    getCallHeader(selectedCall).then(h => { if (alive) setFetchedHeader(h); }).catch(() => {});
    return () => { alive = false; };
  }, [selectedCall, selectedCallHeader, detailKind]);

  // Keep the <audio> element's rate + volume in sync with the controls.
  useEffect(() => { if (audioRef.current) audioRef.current.playbackRate = speed; }, [speed]);
  useEffect(() => { if (audioRef.current) audioRef.current.volume = volume; }, [volume]);

  if (!selectedCall) return null;
  // Opened by id (no snapshot) and the header hasn't landed yet — show the overlay
  // immediately with a loading header so the click always navigates.
  if (!call) {
    return (
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: c.paper }}>
        <View style={{ paddingHorizontal: 26, paddingTop: 16 }}>
          <Pressable onPress={closeCall} style={{ flexDirection: 'row', alignItems: 'center', gap: 5, marginBottom: 10 }}>
            <Icon name="chev-l" size={fs(13)} color={c.ink3} />
            <Text style={{ color: c.ink3, fontSize: fs(11.5), fontFamily: theme.font.sans }}>Calls</Text>
          </Pressable>
          <Text style={{ fontSize: fs(13), color: c.ink3, fontFamily: theme.font.sans }}>Loading call…</Text>
        </View>
      </View>
    );
  }

  // Duration: live → elapsed since call start (counts up). Ended → the real
  // recording length from the API once fetched; before that, the frozen final
  // elapsed (from when it left the live set); else the header snapshot.
  const dur = isLive
    ? Math.max(1, Math.floor((Date.now() - (liveStartMs.current ?? Date.now())) / 1000))
    : Math.max(1, detail.durationSeconds ?? frozenDur.current ?? call.durSec ?? 0);

  const recordingUrl = detail.recordingUrl;

  // Real audio controls (recorded mode).
  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { void a.play(); } else { a.pause(); }
  };
  const seek = (t: number) => {
    const a = audioRef.current;
    const clamped = Math.max(0, Math.min(dur, t));
    if (a) a.currentTime = clamped;
    setPlayT(clamped);
  };

  // For a LIVE call, render the streaming SSE lines; `at` = seconds elapsed from the
  // shared call-start anchor. For a finished call, use the fetched detail transcript
  // (its `at` is the real per-segment startMs → real timestamps).
  // Map the SSE-captured live lines to the render shape (used while live, and as a
  // fallback right after hang-up before the stored transcript is processed).
  const liveTranscript = liveLines.map((l) => ({
    at: Math.max(0, Math.floor((l.at - (liveStartMs.current ?? l.at)) / 1000)),
    sp: l.sp === 'agent' ? 'SK Voice' : 'Caller',
    text: l.text,
    partial: l.partial, // still-forming interim line → render dimmed
  }));
  const transcript = isLive
    ? liveTranscript
    // Ended: prefer the stored/processed transcript; if it hasn't landed yet, keep
    // showing the live-captured lines so the panel doesn't blank on hang-up.
    : (detail.transcript.length ? detail.transcript : liveTranscript);
  // Live transcript auto-follows the newest line; historical follows playback.
  const activeIdx = isLive
    ? transcript.length - 1
    : transcript.reduce((acc, t, i) => (t.at <= playT ? i : acc), 0);

  // Captured/unmatched timestamps → which bars get markers.
  const capturedAt = new Set(detail.captured.map(f => Math.floor((f.at / dur) * N_BARS)));
  const unmatchedAt = new Set(detail.unmatched.map(u => Math.floor((u.at / dur) * N_BARS)));
  const playedBars = Math.floor((playT / dur) * N_BARS);

  return (
    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: c.paper }}>
      {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, paddingHorizontal: 26, paddingTop: 16, paddingBottom: 12 }}>
        <View style={{ minWidth: 0, flexShrink: 1 }}>
          <Pressable onPress={closeCall} style={{ flexDirection: 'row', alignItems: 'center', gap: 5, marginBottom: 7 }}>
            <Icon name="chev-l" size={fs(13)} color={c.ink3} />
            <Text style={{ color: c.ink3, fontSize: fs(11.5), fontFamily: theme.font.sans }}>Calls</Text>
          </Pressable>
          <Text style={{ fontSize: fs(20), fontWeight: '600', letterSpacing: -0.4, color: c.ink, fontFamily: theme.font.sans }}>Call · {call.contact}</Text>
          <Text style={{ fontSize: fs(12), color: c.ink3, marginTop: 3, fontFamily: theme.font.sans }}>
            {call.dir === 'inbound' ? 'Inbound' : 'Outbound'} · SK Voice · {isLive ? fmtDur(dur) : durWords(dur)} · {isLive ? 'live now' : call.outcome === 'completed' ? 'completed' : call.outcome === 'missed' ? 'missed' : 'caller dropped'}
          </Text>
        </View>
        <View style={{ flexDirection: 'row', gap: 9, flexShrink: 0 }}>
          <Button kind="default" label=".txt" icon="download" />
          <Button kind="primary" label="Open lead" icon="external" />
        </View>
      </View>

      {/* LIVE call → a real-time indicator (no seekable player; nothing recorded yet). */}
      {!missed && isLive && (
        <View style={{ marginHorizontal: 26, marginBottom: 14, padding: 16, paddingVertical: 14, borderWidth: 1, borderColor: c.line, borderRadius: 12, backgroundColor: c.card, flexDirection: 'row', alignItems: 'center', gap: 16, ...(theme.shadows.sm as object) }}>
          <View style={{ width: 8, height: 8, borderRadius: 999, backgroundColor: c.ink }} />
          <Text style={{ fontWeight: '700', fontSize: fs(13), color: c.ink, fontFamily: theme.font.sans }}>LIVE</Text>
          {/* Animated equalizer bars driven by the 1s tick. */}
          <View style={{ flex: 1, height: 40, flexDirection: 'row', alignItems: 'center', gap: 3, overflow: 'hidden' }}>
            {Array.from({ length: N_BARS }).map((_, i) => {
              const h = 20 + Math.round(60 * Math.abs(Math.sin((Date.now() / 220) + i * 0.7)));
              return <View key={i} style={{ width: 3, height: `${h}%`, borderRadius: 2, backgroundColor: c.ink3 }} />;
            })}
          </View>
          <Text style={{ fontFamily: theme.font.mono, fontSize: fs(13), color: c.ink, width: fs(46), textAlign: 'right' }}>{fmtDur(dur)}</Text>
        </View>
      )}

      {/* RECORDED call → real audio playback of the actual recording. */}
      {!missed && !isLive && (
        <>
          {/* Hidden real <audio> — the source of truth for playT/dur (RN-Web → DOM). */}
          {recordingUrl ? (
            // react-native-web renders DOM audio; cast through any for RN types.
            <audio
              ref={audioRef as any}
              src={recordingUrl}
              onTimeUpdate={(e: any) => setPlayT(e.target.currentTime)}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              style={{ display: 'none' }}
            />
          ) : null}

          {recordingUrl ? (
            <>
              {/* Playback bar */}
              <View style={{ marginHorizontal: 26, marginBottom: 14, padding: 16, paddingVertical: 14, borderWidth: 1, borderColor: c.line, borderRadius: 12, backgroundColor: c.card, flexDirection: 'row', alignItems: 'center', gap: 16, ...(theme.shadows.sm as object) }}>
                <Pressable onPress={togglePlay} style={{ width: 44, height: 44, borderRadius: 999, backgroundColor: c.ink, alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name={playing ? 'pause' : 'play'} size={17} color={c.paper} />
                </Pressable>
                <View style={{ gap: 3 }}>
                  <Pressable onPress={() => seek(playT - 15)} style={{ height: 20, paddingHorizontal: 7, borderWidth: 1, borderColor: c.line, borderRadius: 6, justifyContent: 'center' }}><Text style={{ fontSize: 10.5, fontFamily: theme.font.mono, color: c.ink3 }}>−15</Text></Pressable>
                  <Pressable onPress={() => seek(playT + 15)} style={{ height: 20, paddingHorizontal: 7, borderWidth: 1, borderColor: c.line, borderRadius: 6, justifyContent: 'center' }}><Text style={{ fontSize: 10.5, fontFamily: theme.font.mono, color: c.ink3 }}>+15</Text></Pressable>
                </View>
                <Text style={{ fontFamily: theme.font.mono, fontSize: 12, color: c.ink3, width: 38 }}>{fmtDur(playT)}</Text>
                {/* Waveform (visual; click to seek the real audio) */}
                <View style={{ flex: 1, height: 56, flexDirection: 'row', alignItems: 'center', gap: 2, overflow: 'hidden' }}>
                  {Array.from({ length: N_BARS }).map((_, i) => {
                    const h = barHeight(i);
                    let bg = c.lineStrong, w = 3;
                    if (i <= playedBars) bg = c.ink3;
                    if (capturedAt.has(i)) { bg = c.ink; w = 5; }
                    const unm = unmatchedAt.has(i);
                    return (
                      <Pressable key={i} onPress={() => seek((i / N_BARS) * dur)}
                        style={{ width: w, height: `${(h / 86) * 100}%`, borderRadius: 2, backgroundColor: unm ? c.paper : bg, borderWidth: unm ? 1.5 : 0, borderColor: c.ink }} />
                    );
                  })}
                </View>
                <Text style={{ fontFamily: theme.font.mono, fontSize: 12, color: c.ink4, width: 38, textAlign: 'right' }}>{fmtDur(dur)}</Text>
                {/* Volume */}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Icon name="volume" size={16} color={c.ink3} />
                  <Pressable onPress={() => setVolume(v => (v > 0.5 ? 0.2 : 0.8))} style={{ width: 70, height: 4, backgroundColor: c.lineStrong, borderRadius: 20, justifyContent: 'center' }}>
                    <View style={{ height: 4, width: `${volume * 100}%`, backgroundColor: c.ink, borderRadius: 20 }} />
                  </Pressable>
                </View>
              </View>

              {/* Speed + legend */}
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 26, paddingBottom: 12 }}>
                <Text style={{ fontSize: 11, color: c.ink4, fontWeight: '500', fontFamily: theme.font.sans }}>Speed</Text>
                {SPEEDS.map(sp => {
                  const on = sp === speed;
                  return (
                    <Pressable key={sp} onPress={() => setSpeed(sp)} style={{ height: 24, paddingHorizontal: 9, borderRadius: 7, backgroundColor: on ? c.ink : 'transparent', borderWidth: on ? 0 : 1, borderColor: c.line, justifyContent: 'center' }}>
                      <Text style={{ fontSize: 11.5, fontWeight: on ? '600' : '500', color: on ? c.paper : c.ink3, fontFamily: theme.font.sans }}>{sp}×</Text>
                    </Pressable>
                  );
                })}
                <View style={{ flex: 1 }} />
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}><View style={{ width: 12, height: 12, backgroundColor: c.ink, borderRadius: 2 }} /><Text style={{ fontSize: 11, color: c.ink3, fontFamily: theme.font.sans }}>Captured field</Text></View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}><View style={{ width: 12, height: 12, backgroundColor: c.paper, borderWidth: 1.5, borderColor: c.ink, borderRadius: 2 }} /><Text style={{ fontSize: 11, color: c.ink3, fontFamily: theme.font.sans }}>Unmatched</Text></View>
              </View>
            </>
          ) : (
            // Recorded call but no recording URL yet (still processing / not recorded).
            <View style={{ marginHorizontal: 26, marginBottom: 14, padding: 16, borderWidth: 1, borderColor: c.line, borderRadius: 12, backgroundColor: c.card }}>
              <Text style={{ fontSize: 12.5, color: c.ink4, fontFamily: theme.font.sans }}>Recording not available for this call.</Text>
            </View>
          )}
        </>
      )}

      {/* Body split */}
      <View
        style={{ flex: 1, flexDirection: 'row', minHeight: 0, borderTopWidth: 1, borderTopColor: c.line }}
        onLayout={(e) => { rowWidth.current = e.nativeEvent.layout.width; }}
      >
        {/* Transcript — width set by the draggable split */}
        <View style={{ width: `${splitPct}%`, minWidth: 0 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, paddingHorizontal: 22, borderBottomWidth: 1, borderBottomColor: c.line }}>
            <Text style={{ fontWeight: '600', fontSize: fs(13.5), color: c.ink, fontFamily: theme.font.sans }}>Transcript</Text>
            {!missed && <Text style={{ fontSize: fs(11), color: c.ink4, fontFamily: theme.font.sans }}>Click any line to seek</Text>}
          </View>
          {missed ? (
            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontSize: fs(14), fontWeight: '600', color: c.ink2, marginBottom: 4, fontFamily: theme.font.sans }}>Call was missed</Text>
              <Text style={{ fontSize: fs(12.5), color: c.ink3, fontFamily: theme.font.sans }}>No transcript — the caller hung up before SK Voice answered.</Text>
            </View>
          ) : (
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingVertical: 12, paddingHorizontal: 18, gap: 2 }}>
              {transcript.length === 0 && isLive && (
                <Text style={{ fontSize: fs(12.5), color: c.ink4, paddingHorizontal: 12, paddingVertical: 8, fontFamily: theme.font.sans }}>Listening… transcript will appear as the call proceeds.</Text>
              )}
              {transcript.map((t, i) => {
                const active = i === activeIdx;
                return (
                  <Pressable key={i} onPress={() => seek(t.at)}
                    style={{ flexDirection: 'row', gap: 12, paddingVertical: 6, paddingHorizontal: 12, borderRadius: 8, backgroundColor: active ? c.paper2 : 'transparent', borderLeftWidth: active ? 2 : 0, borderLeftColor: c.ink }}>
                    <Text style={{ fontFamily: theme.font.mono, fontSize: fs(11), color: c.ink4, width: fs(34), paddingTop: 2 }}>{fmtDur(t.at)}</Text>
                    <Text style={{ width: fs(52), fontSize: fs(12.5), fontWeight: '700', color: t.sp === 'SK Voice' ? c.ink : c.ink3, fontFamily: theme.font.sans }}>{t.sp}</Text>
                    {/* Interim (still-forming) line renders dimmed; final replaces it. */}
                    <Text style={{ flex: 1, fontSize: fs(12), lineHeight: fs(18), color: (t as { partial?: boolean }).partial ? c.ink3 : c.ink, fontFamily: theme.font.sans }}>{t.text}</Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          )}
        </View>

        {/* Draggable divider — drag left/right to resize the two panes */}
        <View
          {...dividerPan.panHandlers}
          style={{ width: 8, flexShrink: 0, alignItems: 'center', justifyContent: 'center', backgroundColor: c.paper2, ...({ cursor: 'col-resize' } as any) }}
        >
          <View style={{ width: 1, flex: 1, backgroundColor: c.line }} />
          <View style={{ position: 'absolute', width: 3, height: 30, borderRadius: 2, backgroundColor: c.lineStrong }} />
        </View>

        {/* Insights rail — takes the remaining width */}
        <ScrollView style={{ flex: 1, minWidth: 0 }} contentContainerStyle={{ padding: 18, gap: 18 }}>
          {/* Summary */}
          <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9 }}>
              <Text style={{ fontWeight: '600', fontSize: fs(13), color: c.ink, fontFamily: theme.font.sans }}>Summary</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, height: 24, paddingHorizontal: 9, borderWidth: 1, borderColor: c.line, borderRadius: 20 }}>
                <Icon name="v-sun" size={12} color={c.ink3} /><Text style={{ fontSize: 10.5, color: c.ink3, fontFamily: theme.font.sans }}>AI · regenerate</Text>
              </View>
            </View>
            <Text style={{ fontSize: fs(12.5), lineHeight: fs(20), color: c.ink2, fontFamily: theme.font.sans }}>{detail.summary}</Text>
          </View>

          {/* Products discussed */}
          {!missed && (
            <View style={{ borderTopWidth: 1, borderTopColor: c.line, paddingTop: 16 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <Text style={{ fontWeight: '600', fontSize: fs(13), color: c.ink, fontFamily: theme.font.sans }}>Products discussed</Text>
                <Text style={{ fontFamily: theme.font.mono, fontSize: 10, color: c.ink4 }}>asked · upsell · cross-sell</Text>
              </View>
              <View style={{ gap: 8, marginBottom: 12 }}>
                {detail.products.map((p, i) => {
                  const declined = p.response === 'declined';
                  return (
                    <Pressable key={i} onPress={() => seek(p.at)} style={{ borderWidth: 1, borderColor: c.line, borderRadius: 8, padding: 10 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, minWidth: 0 }}>
                          <Text style={{ fontSize: 12.5, fontWeight: '600', color: declined ? c.ink4 : c.ink, textDecorationLine: declined ? 'line-through' : 'none', fontFamily: theme.font.sans }}>{p.name}</Text>
                          <View style={{ paddingHorizontal: 6, height: 16, borderRadius: 20, backgroundColor: p.kind === 'ASKED' ? c.paper2 : c.ink, borderWidth: p.kind === 'ASKED' ? 1 : 0, borderColor: c.line, justifyContent: 'center' }}>
                            <Text style={{ fontSize: 8.5, fontWeight: '600', color: p.kind === 'ASKED' ? c.ink3 : c.paper, fontFamily: theme.font.mono }}>{p.kind}</Text>
                          </View>
                        </View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                          <Text style={{ fontFamily: theme.font.mono, fontSize: 12, fontWeight: '600', color: c.ink }}>{inr(p.price)}</Text>
                          <Text style={{ fontFamily: theme.font.mono, fontSize: 10, color: c.ink4 }}>⏱ {fmtDur(p.at)}</Text>
                        </View>
                      </View>
                      <Text style={{ fontSize: 11, color: declined ? c.ink4 : c.ink2, fontFamily: theme.font.sans }}>{declined ? '✗ declined' : '✓ interested'}</Text>
                    </Pressable>
                  );
                })}
              </View>
              <View style={{ flexDirection: 'row', paddingVertical: 12, borderTopWidth: 1, borderTopColor: c.line, borderBottomWidth: 1, borderBottomColor: c.line }}>
                {[['Offered', inr(detail.offered), `${detail.offeredN} items`], ['Accepted', inr(detail.accepted), `${detail.acceptedN} item`], ['Hit rate', `${detail.hitRate}%`, 'of upsells']].map(([l, v, s], i) => (
                  <View key={i} style={{ flex: 1 }}>
                    <Text style={{ fontSize: 9.5, fontWeight: '600', letterSpacing: 0.5, textTransform: 'uppercase', color: c.ink4, marginBottom: 4, fontFamily: theme.font.sans }}>{l}</Text>
                    <Text style={{ fontFamily: theme.font.mono, fontSize: 14, fontWeight: '600', color: c.ink }}>{v}</Text>
                    <Text style={{ fontSize: 10, color: c.ink4, fontFamily: theme.font.sans }}>{s}</Text>
                  </View>
                ))}
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingTop: 11 }}>
                <Text style={{ fontSize: 12, color: c.ink3, fontFamily: theme.font.sans }}>Potential lead value</Text>
                <Text style={{ fontFamily: theme.font.mono, fontSize: 14, fontWeight: '600', color: c.ink }}>{inr(detail.leadValue)}</Text>
              </View>
            </View>
          )}

          {/* Captured fields */}
          {!missed && (
            <View style={{ borderTopWidth: 1, borderTopColor: c.line, paddingTop: 16 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <Text style={{ fontWeight: '600', fontSize: fs(13), color: c.ink, fontFamily: theme.font.sans }}>Captured fields</Text>
                <Text style={{ fontSize: 10, color: c.ink4, fontFamily: theme.font.sans }}>tap ⏱ to seek</Text>
              </View>
              {detail.captured.map((f, i) => (
                <Pressable key={i} onPress={() => seek(f.at)} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingVertical: 8, paddingHorizontal: 4, borderBottomWidth: 1, borderBottomColor: c.line }}>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: 11.5, color: c.ink3 }}>{f.key}</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <Text style={{ fontSize: 12.5, color: c.ink, textAlign: 'right', fontFamily: theme.font.sans }}>{f.val}</Text>
                    {!!f.src && <Text style={{ fontSize: 10, color: c.ink4, fontStyle: 'italic', fontFamily: theme.font.sans }}>{f.src}</Text>}
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3, borderWidth: 1, borderColor: c.line, borderRadius: 5, paddingHorizontal: 5, paddingVertical: 1 }}>
                      <Text style={{ fontFamily: theme.font.mono, fontSize: 10, color: c.ink4 }}>⏱ {fmtDur(f.at)}</Text>
                    </View>
                  </View>
                </Pressable>
              ))}
            </View>
          )}

          {/* Unmatched captured */}
          {!missed && detail.unmatched.length > 0 && (
            <View style={{ borderTopWidth: 1, borderTopColor: c.line, paddingTop: 16 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <Text style={{ fontWeight: '600', fontSize: fs(13), color: c.ink, fontFamily: theme.font.sans }}>Unmatched captured</Text>
                <Text style={{ fontSize: 10, color: c.ink4, fontFamily: theme.font.sans }}>{detail.unmatched.length} phrases</Text>
              </View>
              <View style={{ gap: 7, marginBottom: 10 }}>
                {detail.unmatched.map((u, i) => (
                  <Pressable key={i} onPress={() => seek(u.at)} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingVertical: 8, paddingHorizontal: 11, borderWidth: 1, borderStyle: 'dashed', borderColor: c.lineStrong, borderRadius: 8, backgroundColor: c.paper2 }}>
                    <Text style={{ fontSize: 12, color: c.ink2, fontFamily: theme.font.sans }}>" {u.phrase} "</Text>
                    <Text style={{ fontFamily: theme.font.mono, fontSize: 10, color: c.ink4 }}>⏱ {fmtDur(u.at)}</Text>
                  </Pressable>
                ))}
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: 11, color: c.ink3, fontFamily: theme.font.sans }}>Logged for catalog review</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}><Text style={{ fontSize: 11, fontWeight: '500', color: c.ink, fontFamily: theme.font.sans }}>All unmatched</Text><Icon name="chev-r" size={12} color={c.ink} /></View>
              </View>
            </View>
          )}
        </ScrollView>
      </View>
    </View>
  );
}
